import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { StockPhotoAssetIndexFilters } from '@/lib/services/photo-assets/photo-assets.types'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CreateStockImageInput,
  CreateStockImageTagInput,
  StockImageIdInput,
  StockImageIndexPage,
  StockImageResponse,
  StockImageTagResponse,
  StockImageTagsResponse,
  UpdateStockImageInput,
  UpdateStockImageTagInput,
} from './stock-images.types'

export interface StockImageOrganizationScope {
  organizationId: OrganizationId
}

export interface StockImageIndexQueryKeyEntries extends StockImageOrganizationScope {
  filters?: StockPhotoAssetIndexFilters | undefined
  pageSize?: number | undefined
}

export interface StockImageShowQueryKeyEntries extends StockImageOrganizationScope {
  stockImageId?: StockImageIdInput | undefined
}

type StockImageQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

/** Stock image pages start without a cursor and continue with the last page's `next_cursor`. */
type StockImagePageQueryContext<TKey> = Pick<
  QueryFunctionContext<[TKey], string | null>,
  'queryKey' | 'pageParam'
>

export const getStockImageUploadEndpoint = () =>
  `${import.meta.env.VITE_API_URL}/stock-photo-assets`

/** Multipart requests send every field, empty when omitted. */
function appendStockImageFormData(
  formData: FormData,
  values: UpdateStockImageInput & Pick<CreateStockImageInput, 'seasonal_banner'> = {}
) {
  if (values.file instanceof File) {
    formData.append('file', values.file)
  }

  formData.append('is_published', values.is_published ? '1' : '0')
  if (values.seasonal_banner) formData.append('seasonal_banner', '1')
  formData.append('title', values.title ?? '')
  formData.append('description', values.description ?? '')
  formData.append('tag_ids', JSON.stringify(values.tag_ids ?? []))
  formData.append('occasion_ids', JSON.stringify(values.occasion_ids ?? []))
  formData.append('season_ids', JSON.stringify(values.season_ids ?? []))

  return formData
}

export async function getStockImages({
  queryKey: [{ organizationId, filters = {}, pageSize = 40 }],
  pageParam,
}: StockImagePageQueryContext<StockImageIndexQueryKeyEntries>) {
  const params = {
    ...filters,
    per_page: pageSize,
    ...(pageParam ? { cursor: pageParam } : {}),
  }

  return axios.get<StockImageIndexPage>(
    '/stock-photo-assets',
    buildAdminRequestConfig({ organizationId, params })
  )
}

export async function getStockImage({
  queryKey: [{ organizationId, stockImageId }],
}: StockImageQueryContext<StockImageShowQueryKeyEntries>) {
  return axios.get<StockImageResponse>(
    `/stock-photo-assets/${stockImageId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createStockImage({
  organizationId,
  values,
}: StockImageOrganizationScope & { values: CreateStockImageInput }) {
  const formData = appendStockImageFormData(new FormData(), values)

  return axios.post<StockImageResponse>(
    '/stock-photo-assets',
    formData,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateStockImage({
  organizationId,
  stockImageId,
  values,
}: StockImageOrganizationScope & {
  stockImageId: StockImageIdInput
  values: UpdateStockImageInput
}) {
  if (values.file instanceof File) {
    const formData = appendStockImageFormData(new FormData(), values)
    formData.append('_method', 'PUT')

    return axios.post<StockImageResponse>(
      `/stock-photo-assets/${stockImageId}`,
      formData,
      buildAdminRequestConfig({ organizationId })
    )
  }

  return axios.put<StockImageResponse>(
    `/stock-photo-assets/${stockImageId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function deleteStockImage({
  organizationId,
  stockImageId,
}: StockImageOrganizationScope & { stockImageId: StockImageIdInput }) {
  return axios.delete<string>(
    `/stock-photo-assets/${stockImageId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getStockImageTags({
  queryKey: [{ organizationId }],
}: StockImageQueryContext<StockImageOrganizationScope>) {
  return axios.get<StockImageTagsResponse>(
    '/stock-photo-assets/tags',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createStockImageTag({
  organizationId,
  values,
}: StockImageOrganizationScope & { values: CreateStockImageTagInput }) {
  return axios.post<StockImageTagResponse>(
    '/stock-photo-assets/tags',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateStockImageTag({
  organizationId,
  tagId,
  values,
}: StockImageOrganizationScope & { tagId: number; values: UpdateStockImageTagInput }) {
  return axios.put<StockImageTagResponse>(
    `/stock-photo-assets/tags/${tagId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function deleteStockImageTag({
  organizationId,
  tagId,
}: StockImageOrganizationScope & { tagId: number }) {
  return axios.delete<string>(
    `/stock-photo-assets/tags/${tagId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
